/**
 * The access cookie lives 15 minutes; the refresh cookie lives 7 days. Without this, any page
 * left open past 15 minutes answered "Not authenticated" until the app was reloaded.
 * On a 401 from an ordinary endpoint, spend ONE refresh (shared by concurrent requests) and
 * retry the request once. Sign-in, refresh and sign-out endpoints are never retried.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NO_RETRY = /\/api\/auth\/(login|refresh|logout|register|google|apple|verify|forgot|reset)/;

let inflight = null;

export const refreshSession = () => {
  if (!inflight) {
    inflight = fetch(`${API_URL}/api/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { setTimeout(() => { inflight = null; }, 0); });
  }
  return inflight;
};

export const installSessionRefresh = () => {
  axios.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    const url = config?.url || '';
    if (error?.response?.status !== 401 || !config || config.__sessionRetried || NO_RETRY.test(url)) {
      throw error;
    }
    config.__sessionRetried = true;
    if (!(await refreshSession())) throw error;
    return axios(config);
  });
};

/** fetch() with the same one-refresh-then-retry rule, for downloads that cannot use axios. */
export const fetchWithSession = async (url, options = {}) => {
  const opts = { credentials: 'include', ...options };
  const response = await fetch(url, opts);
  if (response.status !== 401 || NO_RETRY.test(url)) return response;
  if (!(await refreshSession())) return response;
  return fetch(url, opts);
};
